import { Component, ElementRef, HostListener, Input, Output, EventEmitter, ViewChild, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmPopup } from '../confirm-popup/confirm-popup';

export interface AssigneeUser {
  _id: string;
  name: string;
  image?: string;
  assignType?: string;
  email?: string;
  jobrole?: string;
  teamName?: string;
  avatar?: string;
  [key: string]: any; // Allow additional properties
}

export interface AssigneeSelectionConfig {
  title?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  loadingText?: string;
  noResultsText?: string;
  noChangesMessage?: string;
}

@Component({
  selector: 'app-assignee-selection-slider',
  standalone: true,
  imports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    MatIconModule,
    ConfirmPopup
  ],
  templateUrl: './assignee-selection-slider.html',
  styleUrl: './assignee-selection-slider.scss'
})
export class AssigneeSelectionSlider {
  // Input signals using Angular 20 input() function
  @Input() set show(value: boolean) {
    this._show.set(value);
  }
  @Input() set allAssignableData(value: AssigneeUser[]) {
    this._allAssignableData.set(value);
  }
  @Input() set assignedUsers(value: AssigneeUser[]) {
    this._assignedUsers.set(value);
  }
  @Input() set isLoadingData(value: boolean) {
    this._isLoadingData.set(value);
  }
  @Input() set config(value: AssigneeSelectionConfig) {
    this._config.set(value);
  }

  // Output events
  @Output() assignUsers = new EventEmitter<{
    newlySelectedUsers: AssigneeUser[];
    removedUsers: AssigneeUser[];
  }>();
  @Output() close = new EventEmitter<void>();

  // ViewChild
  @ViewChild('assigneeSelect') assigneeSelect!: NgSelectComponent;

  // Private signals
  private _show = signal(false);
  private _allAssignableData = signal<AssigneeUser[]>([]);
  private _assignedUsers = signal<AssigneeUser[]>([]);
  private _isLoadingData = signal(false);
  private _config = signal<AssigneeSelectionConfig>({});

  // Public signals
  selectedUsers = signal<AssigneeUser[]>([]);
  selectedUserIds = signal<string[]>([]);
  assigneeSearch = signal('');
  filteredAssignees = signal<AssigneeUser[]>([]);
  showDropdown = signal(false);
  showNoChangesMessage = signal(false);
  showAssignConfirmPopup = signal(false);
  previouslyAssignedUsers = signal<AssigneeUser[]>([]);
  imageLoadError: { [key: string]: boolean } = {}; // Track broken images

  // Computed signals
  ngSelectItems = computed(() => {
    // Get already selected user IDs
    const selectedUserIdsSet = new Set(this.selectedUserIds());
    
    // Build a map of all available users (including selected ones for display)
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    
    // First, add all assignable data
    this._allAssignableData().forEach(user => {
      uniqueUsersMap.set(user._id, user);
    });
    
    // Also include selected users from assignedUsers to ensure they're available for display
    this._assignedUsers().forEach(user => {
      if (!uniqueUsersMap.has(user._id)) {
        uniqueUsersMap.set(user._id, user);
      }
    });
    
    // If dropdown is open and there's a search term, return filtered results (excluding already selected)
    if (this.showDropdown() && this.assigneeSearch().trim().length > 0) {
      return this.filteredAssignees();
    }
    
    // If dropdown is open, return all users (ng-select needs selected users in items to display them)
    // We'll prevent re-selection in the selection handler
    if (this.showDropdown()) {
      return Array.from(uniqueUsersMap.values());
    }
    
    // Default case: return all users
    return Array.from(uniqueUsersMap.values());
  });

  // Computed config with defaults
  sliderConfig = computed(() => {
    const config = this._config();
    return {
      title: config.title || 'Assign Users',
      placeholder: config.placeholder || 'Search and select assignees...',
      confirmText: config.confirmText || 'Assign Users',
      cancelText: config.cancelText || 'Cancel',
      loadingText: config.loadingText || 'Loading...',
      noResultsText: config.noResultsText || 'No assignees found',
      noChangesMessage: config.noChangesMessage || 'No changes made to assignees'
    };
  });

  constructor() {
    // Effect to handle slider opening
    effect(() => {
      if (this._show()) {
        this.loadSelectedUsers();
        this.resetSliderState();
      }
    });
  }

  // Slider control methods
  openSlider = () => {
    this._show.set(true);
    this.showNoChangesMessage.set(false);
    this.imageLoadError = {}; // Reset image error state when opening slider
    this.loadSelectedUsers();
    this.selectedUsers.set(this.ensureUniqueUsers(this.selectedUsers()));
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(true);
    setTimeout(() => {
      this.clearAssigneeSearch();
       if (this.assigneeSelect) {
        this.assigneeSelect.detectChanges();
      }
    }, 100);
  };

  closeSlider = () => {
    this._show.set(false);
    this.selectedUsers.set([]);
    this.selectedUserIds.set([]);
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(false);
    this.clearAssigneeSearch();
    this.close.emit();
  };

  // User selection methods
  onUserSelectionChange = (userIds: string[]) => {
    // Get the previously selected user IDs (from when modal was opened)
    const previouslySelectedIdsSet = new Set(this.previouslyAssignedUsers().map(u => u._id));
    
    // Filter out any IDs that were already selected when modal opened
    // This prevents re-selecting users that were already assigned
    const validUserIds = userIds.filter(id => {
      // If this user was already selected when modal opened, don't allow re-selection
      // (ng-select might try to add it again, but we prevent it)
      return true; // We'll handle the logic differently - just ensure we have the data
    });
    
    // However, we need to ensure we don't add duplicates
    // Get current selected IDs to check for duplicates
    const currentSelectedSet = new Set(this.selectedUserIds());
    const newIds = validUserIds.filter(id => !currentSelectedSet.has(id));
    
    // If user is trying to select someone already in the current selection, ignore it
    // This handles the case where ng-select tries to add an already selected user
    const finalUserIds = [...new Set(validUserIds)]; // Remove any duplicates
    
    this.selectedUserIds.set(finalUserIds);
    
    // Build user map from all available sources to ensure we have complete user data
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    
    // Add assigned users first (these are the ones already selected)
    this._assignedUsers().forEach(user => {
      const displayName = user.name || user.jobrole || user.teamName || 'Unknown';
      uniqueUsersMap.set(user._id, {
        ...user, // Include all properties first
        _id: user._id,
        name: displayName, // Override with computed display name
        image: user.image || user.avatar || user.image
      });
    });
    
    // Add all assignable data
    this._allAssignableData().forEach(user => {
      if (!uniqueUsersMap.has(user._id)) {
        const displayName = user.name || user.jobrole || user.teamName || 'Unknown';
        uniqueUsersMap.set(user._id, {
          ...user, // Include all properties first
          _id: user._id,
          name: displayName, // Override with computed display name
          image: user.image || user.avatar || user.image
        });
      }
    });
    
    // Map selected IDs to user objects with complete data
    this.selectedUsers.set(
      finalUserIds
        .map(id => uniqueUsersMap.get(id))
        .filter(user => user !== undefined)
        .map(user => {
          const displayName = user!.name || user!.jobrole || user!.teamName || 'Unknown';
          return {
            _id: user!._id,
            name: displayName,
            image: user!.image || user!.avatar,
            assignType: user!.assignType 
          };
        })
    );
    
    this.checkForChanges();
  };

  removeSelectedUser = (index: number) => {
    const arr = [...this.selectedUsers()];
    const removedUser = arr.splice(index, 1)[0];
    this.selectedUsers.set(this.ensureUniqueUsers(arr));
    this.selectedUserIds.set(this.selectedUserIds().filter(id => id !== removedUser._id));
    this.checkForChanges();
  };

  removeSelectedUserFromNgSelect = (item: AssigneeUser, event: Event) => {
    event.stopPropagation(); 
    const arr = [...this.selectedUsers()];
    const index = arr.findIndex(user => user._id === item._id);
    if (index !== -1) {
      arr.splice(index, 1);
      this.selectedUsers.set(this.ensureUniqueUsers(arr));
      this.selectedUserIds.set(this.selectedUserIds().filter(id => id !== item._id));
    }
    this.checkForChanges();
  };

  // Search and filter methods
  filterAssignees = () => {
    const search = this.assigneeSearch().toLowerCase();
    // Get already selected user IDs to filter them out from search results
    const selectedUserIdsSet = new Set(this.selectedUserIds());
    
    // Combine all assignable data for search
    const allUsersMap = new Map<string, AssigneeUser>();
    this._allAssignableData().forEach(user => {
      allUsersMap.set(user._id, user);
    });
    const currentData = Array.from(allUsersMap.values());
    
    // Filter: exclude already selected users from search results
    this.filteredAssignees.set(
      currentData.filter(item => {
        // Exclude already selected users from search dropdown
        if (selectedUserIdsSet.has(item._id)) {
          return false;
        }
        const displayName = this.getDisplayName(item);
        return displayName.toLowerCase().includes(search);
      })
    );
  };

  onAssigneeSearch = (event: any) => {
    this.assigneeSearch.set(event.term);
    if (this.assigneeSearch() && this.assigneeSearch().trim().length > 0) {
      this.showDropdown.set(true);
      this.filterAssignees();
    } else {
      this.showDropdown.set(false);
      this.filteredAssignees.set([]);
    }
  };

  // NgSelect event handlers
  onAssigneeAdd = () => this.forceClearSearchWithDelay();
  onAssigneeRemove = () => {};
  onAssigneeSelectionChanges = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(false);
    setTimeout(() => {
      this.clearAssigneeSearch();
    }, 5);
  };
  onAssigneeSelect = () => this.forceClearSearchWithDelay();
  onAssigneeChange = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set(this._allAssignableData());
    this.showDropdown.set(false);
    this.clearAssigneeSearch();
  };
  onAssigneeBlur = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set(this._allAssignableData());
    // Keep dropdown open to show all assignees
    // this.showDropdown.set(false);
    this.clearAssigneeSearch();
  };
  onAssigneeOpen = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(true);
  };
  onAssigneeClose = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(false);
    this.clearAssigneeSearch();
  };

  // Search clearing methods
  clearAssigneeSearch = () => {
    if (this.assigneeSelect) {
      this.assigneeSelect.searchTerm = '';
      if (this.assigneeSelect.searchInput && this.assigneeSelect.searchInput.nativeElement) {
        const searchInput = this.assigneeSelect.searchInput.nativeElement;
        searchInput.value = '';
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.assigneeSelect.detectChanges();
      if (this.assigneeSelect.searchInput) {
        this.assigneeSelect.searchInput.nativeElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
      }
    }
    this.filteredAssignees.set([]);
    // Don't close dropdown when clearing search - let it stay open to show all assignees
    // this.showDropdown.set(false);
  };

  forceClearSearch = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    // Keep dropdown open to show all assignees
    // this.showDropdown.set(false);
    if (this.assigneeSelect && this.assigneeSelect.searchInput) {
      const searchInput = this.assigneeSelect.searchInput.nativeElement;
      searchInput.value = '';
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      searchInput.dispatchEvent(new Event('change', { bubbles: true }));
      searchInput.dispatchEvent(new Event('blur', { bubbles: true }));
    }
  };

  forceClearSearchWithDelay = () => {
    setTimeout(() => {
      this.forceClearSearch();
    }, 1);
  };

  // Assignment confirmation methods
  onAssignUsersClick = () => this.showAssignConfirmPopup.set(true);
  
  confirmAssignUsers = () => {
    this.showAssignConfirmPopup.set(false);
    this._assignUsersWithApi();
  };
  
  cancelAssignUsers = () => this.showAssignConfirmPopup.set(false);

  // Helper methods
  private ensureUniqueUsers(users: AssigneeUser[]): AssigneeUser[] {
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    users.forEach(user => {
      uniqueUsersMap.set(user._id, user);
    });
    return Array.from(uniqueUsersMap.values());
  }

  private loadSelectedUsers = () => {
    this.previouslyAssignedUsers.set([...this._assignedUsers()]);
    this.selectedUsers.set([]);
    this.selectedUserIds.set([]);
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    this._assignedUsers().forEach(user => {
      // Ensure we have a proper name using getDisplayName logic
      const displayName = user.name || user.jobrole || user.teamName || (user as any)['jobroles']?.jobrole || 'Unknown';
      uniqueUsersMap.set(user._id, {
        ...user, // Include all properties first
        _id: user._id,
        name: displayName, // Override with computed display name
        image: user.image || user.avatar || user.image
      });
    });
    const arr = Array.from(uniqueUsersMap.values());
    this.selectedUsers.set(arr);
    this.selectedUserIds.set(arr.map(user => user._id));
  };

  private resetSliderState = () => {
    this.showNoChangesMessage.set(false);
    this.showAssignConfirmPopup.set(false);
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(false);
    this.imageLoadError = {}; // Reset image error state
  };

  private checkForChanges = () => {
    const newlySelectedUsers = this.selectedUsers().filter(selectedUser => 
      !this.previouslyAssignedUsers().some(prevUser => prevUser._id === selectedUser._id));
    const removedUsers = this.previouslyAssignedUsers().filter(prevUser => 
      !this.selectedUsers().some(selectedUser => selectedUser._id === prevUser._id));
    
    const hasChanges = newlySelectedUsers.length > 0 || removedUsers.length > 0;
    this.showNoChangesMessage.set(!hasChanges);
  };

  private _assignUsersWithApi = () => {
    const newlySelectedUsers = this.selectedUsers().filter(selectedUser => 
      !this.previouslyAssignedUsers().some(prevUser => prevUser._id === selectedUser._id))
        .map(selectedUser => ({...selectedUser, assignType: selectedUser.assignType}));
    const removedUsers = this.previouslyAssignedUsers().filter(prevUser => 
      !this.selectedUsers().some(selectedUser => selectedUser._id === prevUser._id))
      .map(prevUser => ({...prevUser, assignType: prevUser.assignType}));
    
    if (newlySelectedUsers.length === 0 && removedUsers.length === 0) {
      this.showNoChangesMessage.set(true);
      return;
    }

    this.assignUsers.emit({
      newlySelectedUsers,
      removedUsers
    });
    this.closeSlider();
  };

  getDisplayName = (item: any): string => { 
    if (!item) return 'Unknown';
    return item.name || item.jobrole || item.teamName || item.jobroles?.jobrole || 'Unknown';
  };

  // Get assignee type (PEOPLE, TEAM, JOBROLE)
  getAssigneeType(item: any): 'PEOPLE' | 'TEAM' | 'JOBROLE' {
    if (!item) return 'PEOPLE';
    
    // Check assignType first if available
    if (item.assignType) {
      if (item.assignType === 'TEAM' || item.assignType === 'team') return 'TEAM';
      if (item.assignType === 'JOBROLE' || item.assignType === 'jobrole' || item.assignType === 'JOB_ROLE') return 'JOBROLE';
      return 'PEOPLE';
    }
    
    // Check by properties - if it has teamName, it's likely a team
    if (item.teamName && !item.name && !item.jobrole) {
      return 'TEAM';
    }
    
    // If it has jobrole but no name, it's likely a job role
    if (item.jobrole && !item.name && !item.teamName) {
      return 'JOBROLE';
    }
    
    // If it has email or image, it's likely a person
    if (item.email || item.image || item.avatar) {
      return 'PEOPLE';
    }
    
    // Default to PEOPLE
    return 'PEOPLE';
  }

  // Get type label for display
  getTypeLabel(item: any): string {
    const type = this.getAssigneeType(item);
    switch (type) {
      case 'TEAM':
        return 'Team';
      case 'JOBROLE':
        return 'Job Role';
      case 'PEOPLE':
      default:
        return 'Member';
    }
  }

  // Get type badge class
  getTypeBadgeClass(item: any): string {
    const type = this.getAssigneeType(item);
    switch (type) {
      case 'TEAM':
        return 'type-badge type-badge-team';
      case 'JOBROLE':
        return 'type-badge type-badge-jobrole';
      case 'PEOPLE':
      default:
        return 'type-badge type-badge-person';
    }
  }

  // Get user initials for avatar placeholder
  getInitials(name: string): string {
    if (!name || name.trim() === '') {
      return '?';
    }

    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  // Generate consistent avatar color based on user ID
  getAvatarColor(userId: string): string {
    if (!userId) return '#6b7280';
    
    // Rich vibrant colors matching notification listing
    const colors = [
      '#8b5cf6', // Purple
      '#10b981', // Green
      '#f59e0b', // Yellow/Amber
      '#3b82f6', // Blue
      '#ef4444', // Red
      '#ec4899', // Pink
      '#14b8a6', // Teal
      '#f97316', // Orange
      '#6366f1', // Indigo
      '#a855f7', // Purple
      '#06b6d4', // Cyan
      '#84cc16', // Lime
    ];

    // Generate hash from user ID
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  }

  // Check if image should be shown (has image and not broken)
  hasValidImage(user: AssigneeUser): boolean {
    const imageUrl = user.image || user.avatar;
    return !!(imageUrl && !this.imageLoadError[user._id]);
  }

  // Handle image loading errors
  onImageError(userId: string): void {
    this.imageLoadError[userId] = true;
  }

  // Click outside handler
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this._show()) {
      const target = event.target as HTMLElement;
      if (!target.closest('.assign_modal') && !target.closest('.ts_add_card')) {
        this.closeSlider();
      }
    }
  }

  // Getters for template access
  get show() {
    return this._show();
  }

  get allAssignableData() {
    return this._allAssignableData();
  }

  get assignedUsers() {
    return this._assignedUsers();
  }

  get isLoadingData() {
    return this._isLoadingData();
  }
} 